// Copyright 2023 Sorama B.V.
import {
    autoinject,
    bindable,
    BindingEngine,
    computedFrom,
    Disposable,
    observable,
    transient,
} from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { Grid } from "controls/plot/grid-plot/GridData";
import { Margin } from "controls/plot/IPlotPart";
import { PositionedLegendGroup } from "controls/plot/parts/LegendPart";
import { Quad } from "datatypes/Generic/Quad";
import {
    GenericMeasurement,
    HolographyProjection,
    HolographyProjectionAnalysisProperties,
    HolographyProjectionAnalysisProperty,
    HolographyProjectionHologramAnalysisProperties,
    HolographyProjectionMode,
    MeasurementPartType,
    Point3D,
    SpatialEnergyCurveSetCurve,
    TargetAndSourceRegionLinkProperties,
} from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { CSVDataExporter } from "libraries/Export/CSVDataExporter";
import { IAnimator } from "libraries/Export/IAnimator";
import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";
import { IImageRetriever, Image, ImageStorageType } from "services/Image/IImageService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IHologramProcessingService } from "services/SoundProcessing/IHologramProcessingService";
import {
    IHolographyHologramProcessingService,
    IHolographyProcessingService,
} from "services/SoundProcessing/IHolographyProcessingService";

import { Frequency } from "units/Frequency";
import { Length } from "units/Length";
import { Phase } from "units/Phase";
import { Time } from "units/Time";
import { Unit, UnitValue } from "units/Unit";

import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { GrayscaleColorMap } from "services/ColorMap/Predefined/GrayscaleColorMap";
import { JetColorMap } from "services/ColorMap/Predefined/JetColorMap";

import { URA } from "libraries/Math/URA";

import { Size } from "libraries/DataTypes/Size";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import {
    IWorkflowModuleActivate,
    IWorkflowModuleDeactivate,
    IWorkflowModuleExportData,
    IWorkflowModuleExportVisual,
    IWorkflowModuleInstance,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ExportModalState, IExportModule } from "modules/modals/Export/ExportModal";
import { ImageExportState, LogoPositions } from "modules/modals/ImageExport/ImageExportModal";
import { ImagePickerModalState } from "modules/modals/ImagePicker/ImagePickerModal";
import { ImageTransformModalState } from "modules/modals/ImageTransform/ImageTransformModal";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { NearfieldProjectionSetModel } from "./NearfieldProjectionSetModel";

// The state of the one of the projection modes in the nearfield module
interface ProjectionState {
    // Which projection is being computed. 3 options: Pressure, Velocity, Intensity
    mode: HolographyProjectionMode;

    // Whether the user selected the projection mode in the ui
    selected: boolean;

    // The calculation of the Projection if it has been computed. (serves as cache)
    calculatedProjection: HolographyProjection;

    // The display name of the projection mode
    name: string;
}

@transient()
@autoinject
export class NearfieldModule
    implements
        IWorkflowModuleActivate,
        IWorkflowModuleExportData,
        IWorkflowModuleDeactivate,
        IWorkflowModuleExportVisual,
        IExportModule
{
    //#region Constants
    private static dataColorMapSettingKey = "NearfieldAnalysisModuleDataColorMap";
    private static kspaceIndicatorColormap = new GrayscaleColorMap();
    private moduleInstance: IWorkflowModuleInstance;
    private nearfieldSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<NearfieldModule>, void>;
    private gridPartSize: Size = { width: 0, height: 0 };

    private matrixSize: Size = { width: 0, height: 0 };

    //#endregion

    //#region  Module properties

    @computedFrom("currentProjections", "grid")
    public get disableExport(): boolean {
        return !this.currentProjections || !this.grid;
    }

    @computedFrom("animator")
    public get canExportVideo(): boolean {
        return !!this.animator;
    }

    @computedFrom("currentProjections.gridTemplate")
    public get gridTemplate(): string {
        return this.currentProjections ? this.currentProjections.gridTemplate : "";
    }

    @computedFrom("currentProjections.gridAspectSize")
    public get gridAspectSize(): Size {
        const result = this.currentProjections ? this.currentProjections.gridAspectSize : { width: 1, height: 1 };
        return result;
    }

    @computedFrom("currentProjections.gridMargin")
    public get gridMargin(): Margin {
        return this.currentProjections ? this.currentProjections.gridMargin : new Margin(0, 0, 0, 0);
    }

    @computedFrom("gridAspectSize")
    public get aspectRatio(): number {
        return this.gridAspectSize ? this.gridAspectSize.width / this.gridAspectSize.height : 1;
    }

    @computedFrom("distanceValue")
    public get distance(): number {
        return this.distanceValue;
    }

    public set distance(value) {
        this.distanceValue = value;

        const measurementDistance = this.computedMeasurement?.distance ?? this.selectedMeasurement?.distance ?? 0;
        this.distanceInput = measurementDistance - value;
    }

    //#endregion

    //#region UI Elements

    public fullscreenElement: HTMLElement;
    private animator: IAnimator;

    //#endregion

    //#region UI Data
    public localPlayableId: string;
    public globalPlayableId: string = IPlayablePropertiesService.GlobalPhasePlayablePropertyId;
    public playableId = IPlayablePropertiesService.GlobalPhasePlayablePropertyId;

    public showStitched = true;

    // Signal to Noise Ratio which uses cutoff - Always true but we keep this variable to enable us to switch back to the traditional cutoff filter
    @observable({ changeHandler: "useSNRChanged" })
    @userSetting()
    public useSNR = true;

    // The properties used for the holography computed results
    private usedProperties: HolographyProjectionAnalysisProperties;

    // Store the last used properties for the holography computed results.
    private get usedFrequency(): number {
        return this.usedProperties?.properties[0].frequency;
    }

    private get usedDistance(): number {
        return this.usedProperties?.properties[0].distance;
    }

    // Used in the UI to determine if we used the automatic cutoff for the computed results
    private get usedAutomaticCutoff(): boolean {
        return this.usedProperties?.properties[0].useAutomaticCutoff;
    }

    // We use this value in updateData to determine if we need to clear the cache of the holography projections
    private shouldClearProjectionCacheOnNewCompute = false;

    // Return boolean to tell if the visualization needs to be updated because properties have changed
    public get needsToUpdateAnalysis(): boolean {
        // Early exit if we have no properties yet computed
        if (!this.usedProperties) return true;

        // We need to clear the cache if we have a new measurement - unless we reach the point where we have compared all projections
        let newGeneratedInput = this.generateHolographyProjectionAnalysisProperties();
        if (!newGeneratedInput) return true;

        // Check new properties against old properties if a projection mode is selected
        if (newGeneratedInput.properties.length > 0) {
            if (this.usedProperties.properties.length === 0) return true;
            this.shouldClearProjectionCacheOnNewCompute = true;

            // Can take first mode as all modes have the same properties.
            const oldProps = this.usedProperties?.properties[0];
            const newProps = newGeneratedInput?.properties[0];
            if (this.havePropertiesChanged(oldProps, newProps)) return true;

            // All properties are the same, so we don't need to clear the projection cache as they are the same
            this.shouldClearProjectionCacheOnNewCompute = false;
        }

        // Finally, check if we have a projection mode that is not yet computed, but we want to display it
        if (this.hasUncomputedProjections(newGeneratedInput)) return true;
        return false;
    }

    private havePropertiesChanged(
        oldProps: HolographyProjectionAnalysisProperty,
        newProps: HolographyProjectionAnalysisProperty
    ): boolean {
        if (oldProps == undefined || newProps == undefined) return true;

        // Distance can be undefined, so we need to check for that
        if (oldProps.distance == undefined || newProps.distance == undefined) return true;
        if (Math.abs(oldProps.distance - newProps.distance) > 1e-4) return true;
        if (Math.abs(oldProps.frequency - newProps.frequency) > 1e-4) return true;

        // Verify slope and SNR - Currently is always the same.
        if (oldProps.slope !== newProps.slope) return true;
        if (oldProps.useSNR !== newProps.useSNR) return true;

        // Cutoff evaluation
        if (oldProps.useAutomaticCutoff !== newProps.useAutomaticCutoff) return true;
        // Only when using manual cutoff, we care about the cutoff value changing.
        if (!newProps.useAutomaticCutoff) {
            if (Math.abs(oldProps.cutoff - newProps.cutoff) > 0.5) return true;
        }

        return false;
    }

    private useSNRChanged(newValue: boolean, oldValue: boolean): void {
        if (oldValue === undefined || newValue === oldValue) return;

        if (oldValue && !newValue) {
            // From new SNR to Legacy: Reset to minimum valid rad/m value
            this.cutoff = 0;
            this.unlockedFilter = true;
        }

        // From Legacy to new SNR: Reset to default dB value
        this.cutoff = 50;

        this.dispatchUpdateData();
    }

    private hasUncomputedProjections(newGeneratedInput: HolographyProjectionAnalysisProperties): boolean {
        for (const projection of newGeneratedInput.properties) {
            const projectionState = this.projectionStates.find((x) => x.mode === projection.mode);
            if (!projectionState.calculatedProjection) return true;
        }
        return false;
    }

    // UI fields for the projection modes and to toggle it.
    get hasNoProjectionModeSelected(): boolean {
        return this.projectionStates.every((x) => !x.selected);
    }

    @computedFrom("selectedMeasurement", "useSNR", "selectedMeasurement")
    public get maximumCutoff(): number {
        if (this.useSNR) return 100;
        if (!this.selectedMeasurement) return 153; // Default for 2 cm spacing

        // Determine URA properties of the measurement
        const measurementPoints = this.selectedMeasurement.parts
            .filter((x) => x.measurementType === MeasurementPartType.Audio)
            .map((part) =>
                part.device.sensorPositions.map(
                    (sensor) =>
                        <Point3D>{
                            x: sensor.x + part.position.x,
                            y: sensor.y + part.position.y,
                            z: sensor.z + part.position.z,
                        }
                )
            )
            .reduce((p, c) => p.concat(c));

        const uraProps = URA.getUraProperties3D(measurementPoints);
        if (!uraProps.isUra) return 153;

        return Math.PI / uraProps.spacing;
    }

    @computedFrom("lowerboundCutoff", "unlockedFilter")
    public get minimumCutoff(): number {
        return this.unlockedFilter ? 0 : this.lowerboundCutoff;
    }

    @computedFrom("currentSliderColorCurve", "unlockedFilter", "currentFrequency", "useSNR")
    public get colorMapBackground(): string {
        const fallbackColor = "";
        const warningColor = "black";

        if (
            !this.currentSliderColorCurve ||
            Math.abs(this.usedFrequency - this.frequencySelectionService.selectedFrequency) > 1e-4
        ) {
            // Clear
            return fallbackColor;
        }

        const maximumCutoff = this.maximumCutoff;
        if (!maximumCutoff) {
            return fallbackColor;
        }

        const minimumCutoff = this.minimumCutoff;
        const lowerboundCutoff = this.lowerboundCutoff;
        let linGradient = "linear-gradient(to right";

        // Add stops
        if (!this.maximumCutoff) {
            linGradient = fallbackColor;
        } else {
            linGradient += "," + warningColor + " 0%";
            linGradient +=
                "," +
                warningColor +
                " " +
                (((lowerboundCutoff - minimumCutoff) / (maximumCutoff - minimumCutoff)) * 100).toFixed(0) +
                "%";

            const colorCurve = this.currentSliderColorCurve.data;
            for (let i = 0; i < colorCurve.length; i++) {
                const color = NearfieldModule.kspaceIndicatorColormap.toColor(colorCurve[i].y);
                const r = Math.min(Math.max(Math.round(color.r * 255), 0), 255);
                const g = Math.min(Math.max(Math.round(color.g * 255), 0), 255);
                const b = Math.min(Math.max(Math.round(color.b * 255), 0), 255);

                const stop =
                    Math.min(Math.max((colorCurve[i].x - minimumCutoff) / (maximumCutoff - minimumCutoff), 0), 1) * 100;

                linGradient += ",rgb(" + r + "," + g + "," + b + ") " + stop.toFixed(0) + "%";
            }

            linGradient += ")";
        }

        return linGradient;
    }

    @computedFrom("selectedFrequency", "useSNR")
    private get lowerboundCutoff(): number {
        if (this.useSNR) return 0;
        return (2 * Math.PI * this.frequencySelectionService.selectedFrequency) / 343; // speed of sound
    }

    @computedFrom("unlockedFilter", "currentFrequency")
    private get cutoffTooltipText(): string {
        if (this.minimumCutoff > this.maximumCutoff && !this.unlockedFilter) {
            return "Go to 'Settings / Cutoff Filter' to unlock cutoff frequency filter lowerbound.";
        } else if (this.lowerboundCutoff > this.minimumCutoff && this.unlockedFilter) {
            // When the cutoff filter is unlocked and colorMapBackground starts with a black bar
            return "Applying the cutoff into the black area can be used to filter propagating waves.";
        }
        return "";
    }

    //#endregion

    //#region Fields
    private selectedMeasurementId: string;
    private selectedMeasurement: GenericMeasurement;
    private computedMeasurement: GenericMeasurement;

    public backgroundImage: Image;
    public backgroundImageUri: string;
    public backgroundImageRoi: Quad;

    private selectedImageChangedPromise: Promise<void>;
    private backgroundImageUpdatePromise: Promise<void>;

    private measurementHasChanged: boolean;

    public currentProjections: NearfieldProjectionSetModel;

    @bindable
    public projectionStates: ProjectionState[];

    private idealAspectRatio = 1;

    private distanceValue: number;

    @observable({ changeHandler: "distanceInputChanged" })
    private distanceInput = 0;

    private useDefaultDistance: boolean;
    private maximumDistanceUnlocked: boolean;

    // Cutoff value is rad/m when traditional cutoff filter, and dB when using the SNR cutoff filter
    @observable
    @userSetting()
    private cutoff = 50;

    // The computed cutoff from the API call.
    private computedCutoff: number;

    // Slope is not used in the SNR cutoff filter
    private slope = 0.3;

    @observable
    @userSetting()
    private useAutomaticCutoff = true;

    @observable
    private currentSliderColorCurve: SpatialEnergyCurveSetCurve;

    @userSetting() public opacity = 75;
    @userSetting() public threshold = 30;
    @userSetting() public ticks = false;
    @userSetting() public showLegend = true;
    @userSetting() public legendPosition = 0;

    @observable
    public unlockedFilter = false;
    public progress = 0;

    public legendArray: PositionedLegendGroup[] = [];

    private busy: boolean;

    public grid: Grid;
    public fixedMinimum: UnitValue;
    public fixedMaximum: UnitValue;

    public phaseDisplayUnit: Unit = Phase.degree;
    public baseSpeed: UnitValue = new UnitValue(120, Phase.degree);

    public currentPhase: UnitValue;

    private measurementUpdateDisposable: Disposable;
    private observerSubscriptions: Disposable[] = [];
    public aspectSize: Size = { width: 2, height: 1 };

    @observable
    private dataColorMap: IColorMap = new JetColorMap();

    private imagePartsAndSize = [];

    private partsDimension = [];

    public imageState: { state: ImageExportState; moduleInstance: IWorkflowModuleInstance } = {
        state: null,
        moduleInstance: null,
    };

    private nfExportModal: IWorkflowModuleModalInstance<ExportModalState<IExportModule>, void>;

    //#endregion

    //#region Workflow Properties
    @workflowProperty({
        scope: WorkflowPropertyScope.workflow,
        bindingMode: WorkflowPropertyBindingMode.oneWay,
        objectType: "string",
    })
    public selectedMeasurementIds: string[] = [];
    //#endregion

    //#region Constructors and Lifecycle
    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly measurementProcessorResolver: IMeasurementProcessorResolver,
        private readonly holographyHologramProcessor: IHolographyHologramProcessingService,
        private readonly imageService: IImageRetriever,
        private readonly colorMapService: IColorMapService,
        private readonly resizeListener: ResizeListener,
        private readonly frequencySelectionService: FrequencySelectionService,
        private readonly bindingEngine: BindingEngine
    ) {
        // Set a default state object where only Pressure is enabled and nothing is computed yet
        this.projectionStates = [
            {
                mode: HolographyProjectionMode.Pressure,
                selected: true,
                calculatedProjection: undefined,
                name: "Pressure",
            },
            {
                mode: HolographyProjectionMode.Velocity,
                selected: false,
                calculatedProjection: undefined,
                name: "Velocity",
            },
            {
                mode: HolographyProjectionMode.Intensity,
                selected: false,
                calculatedProjection: undefined,
                name: "Intensity",
            },
        ];
    }

    public bind(): void {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        // Set flag to use the default distance for a measurement
        this.useDefaultDistance = true;

        this.selectedMeasurementIdsChanged();
        this.dispatchUpdateData();
    }

    public deactivateModule(): void {
        if (this.measurementUpdateDisposable) {
            this.measurementUpdateDisposable.dispose();
            this.measurementUpdateDisposable = undefined;
        }

        while (this.observerSubscriptions.length > 0) {
            this.observerSubscriptions.pop().dispose();
        }
    }

    //#endregion

    //#region UI Callbacks
    public toggleSelectedProjection(projection: ProjectionState): void {
        projection.selected = !projection.selected;
        this.updateVisibleProjections();
    }

    public setDefaultDistance(): void {
        this.useDefaultDistance = true;
        if (this.computedMeasurement) this.distance = this.computedMeasurement.distance;
    }

    public unlockMaximumDistance(): void {
        this.maximumDistanceUnlocked = !this.maximumDistanceUnlocked;
        if (!this.maximumDistanceUnlocked) {
            this.setDefaultDistance();
        }
    }

    public dispatchUpdateData(): void {
        this.moduleInstance.dispatch(() => {
            this.updateData();
        });
    }

    public async selectImage(): Promise<void> {
        let selectedImage: Image = this.backgroundImage;

        if (!selectedImage && this.computedMeasurement && this.computedMeasurement.selectedImage) {
            selectedImage = await this.imageService.getImage(this.computedMeasurement.selectedImage);
        }

        await this.moduleInstance
            .createModal<ImagePickerModalState>(WorkflowModalCatalog.ImagePickerModal)
            .withState({
                onSelection: (image, roi) => {
                    if (!image || image.type === ImageStorageType.Cloud) {
                        // Update background image on measurement
                        this.updateBackgroundImageForMeasurement(image, roi);
                    } else {
                        // Just update the background image for the UI, do not save to measurement
                        this.updateBackgroundImageForUI(image ? image.id : undefined, roi);
                    }
                },
                onEdit: (image, roi) => {
                    this.editImage(image, roi);
                },
                measurement: this.selectedMeasurement,
                selectedImage: selectedImage,
            })
            .show();
    }

    public async editImage(image?: Image, roi?: Quad) {
        let backgroundImage;
        let uri;

        if (image) {
            backgroundImage = image;
            uri = await this.imageService.getImageUri(image);
            this.backgroundImageUri = uri;
            this.backgroundImageRoi = roi;
        } else {
            backgroundImage = this.backgroundImage;
            uri = this.backgroundImageUri;
        }
        if (!backgroundImage || !uri) {
            return;
        }
        this.moduleInstance
            .createModal<ImageTransformModalState>(WorkflowModalCatalog.ImageTransformModal)

            .withState({
                image: this.backgroundImageUri,
                measurement: this.computedMeasurement,
                regionOfInterest: this.backgroundImageRoi,
                onRegionOfInterestChanged: (newRegion: Quad) => {
                    if (this.backgroundImage.type === ImageStorageType.Cloud) {
                        this.updateBackgroundImageForMeasurement(backgroundImage, newRegion);
                    } else {
                        this.updateBackgroundImageForUI(backgroundImage, newRegion);
                    }
                },
            })
            .show();
    }
    //#endregion

    //#region Change Callbacks

    public unlockedFilterChanged() {
        if (this.minimumCutoff > this.maximumCutoff) {
            this.cutoff = Math.round(this.maximumCutoff);
        }
    }

    public selectedMeasurementIdsChanged(): void {
        if (!this.selectedMeasurementIds || this.selectedMeasurementIds.length == 0) {
            // Clear data
            this.selectedMeasurementId = undefined;
            this.selectedMeasurement = undefined;
            this.computedMeasurement = undefined;
            this.currentProjections = undefined;
            return;
        }

        if (!this.moduleInstance) return;

        this.measurementHasChanged = this.selectedMeasurementIds[0] !== this.selectedMeasurementId;
        this.selectedMeasurementId = this.selectedMeasurementIds[0];

        if (this.measurementHasChanged) {
            // Clear projectionCache
            this.currentProjections = undefined;
            this.projectionStates.forEach((x) => (x.calculatedProjection = undefined));

            // Fetch a new measurement
            this.moduleInstance
                .dispatch(() => {
                    this.busy = true;

                    return this.measurementRepository.getMeasurement(this.selectedMeasurementId).then((measurement) => {
                        this.selectedMeasurement = measurement;

                        if (!this.unlockedFilter && this.cutoff < this.minimumCutoff) {
                            this.cutoff = this.minimumCutoff;
                        }

                        // Register listener for changes on the measurement
                        if (this.measurementUpdateDisposable) {
                            this.measurementUpdateDisposable.dispose();
                            this.measurementUpdateDisposable = undefined;
                        }

                        this.measurementUpdateDisposable = this.measurementRepository.measurementUpdateObserver(
                            this.measurementUpdated,
                            measurement.isComputedMeasurement ? measurement.computedMeasurementSourceId : measurement.id
                        );

                        if (!this.distance) {
                            this.distance = this.selectedMeasurement.distance;
                        }

                        this.busy = false;

                        // Trigger a data update
                        return this.updateData();
                    });
                })
                .catch(() => {});
        }
    }

    // Based on the current UI state, generate the properties for the holography projection analysis
    private generateHolographyProjectionAnalysisProperties(): HolographyProjectionAnalysisProperties {
        if (!this.selectedMeasurement) return undefined;
        // Determine compute properties
        const computeProps = <HolographyProjectionAnalysisProperties>{
            properties: this.projectionStates
                .filter((x) => x.selected)
                .map((set) => {
                    return {
                        cutoff: this.cutoff,
                        distance: Number(this.useDefaultDistance ? this.selectedMeasurement.distance : this.distance),
                        frequency: this.frequencySelectionService.selectedFrequency,
                        slope: this.slope,
                        useSNR: this.useSNR,
                        useAutomaticCutoff: this.useAutomaticCutoff,
                        mode: set.mode,
                    };
                }),
        };
        return computeProps;
    }

    public measurementUpdated = (property: MeasurementField, measurement: GenericMeasurement): void => {
        // Update measurement fields
        MeasurementExtensions.updateMeasurementField(
            this.selectedMeasurement,
            measurement.id,
            property,
            measurement[property]
        );

        // Check if we need to invalidate some data
        if (
            property === MeasurementField.mutedSensors &&
            MeasurementExtensions.isRelatedTo(this.selectedMeasurement, measurement.id)
        ) {
            this.measurementHasChanged = true;
        }

        if (property === MeasurementField.selectedImage) {
            // Find region for this image
            this.selectedMeasurement.links = measurement.links;
            this.selectedMeasurement.selectedImage = measurement.selectedImage;

            const imageId = this.selectedMeasurement.selectedImage;
            const link = this.selectedMeasurement.links.find((x) => x.mediaObjectId === imageId);

            let roi: Quad = Quad.unitQuad;
            if (link) {
                const props = <TargetAndSourceRegionLinkProperties>link.properties;
                if (props && props.sourceRegion) {
                    roi = props.sourceRegion;
                }
            }
            this.updateBackgroundImageForUI(imageId, roi);
        }

        if (
            property == MeasurementField.distance &&
            MeasurementExtensions.isRelatedTo(this.selectedMeasurement, measurement.id)
        ) {
            this.measurementHasChanged = true;
        }
    };

    public distanceChanged(): void {
        // If the distance was changed far enough from the default distance, we unset useDefaultDistance distance anymore
        if (
            this.useDefaultDistance &&
            this.computedMeasurement &&
            Math.abs(this.distance - this.computedMeasurement.distance) > Number.EPSILON * 2
        ) {
            this.useDefaultDistance = false;
        }
    }

    public dataColorMapChanged(): void {
        if (this.colorMapService) {
            this.colorMapService.setDefaultColorMap(NearfieldModule.dataColorMapSettingKey, this.dataColorMap);
        }
    }

    // Callback for when the selected image has been updated on the measurement object and the visual state needs to be updated
    private updateBackgroundImageForUI(imageOrId: Image | string, region: Quad): void {
        const promise = this.selectedImageChangedPromise ? this.selectedImageChangedPromise : Promise.resolve();

        this.backgroundImageUpdatePromise = promise.then(async () => {
            let image: Image = undefined;
            let id: string = undefined;

            if (imageOrId) {
                if (typeof imageOrId === "string") {
                    id = imageOrId as string;
                } else {
                    image = imageOrId as Image;
                    id = image.id;
                }
            }

            if (!id) {
                this.backgroundImageUri = undefined;
                this.backgroundImageRoi = undefined;
                this.backgroundImage = undefined;
                return;
            }

            if (this.backgroundImage && this.backgroundImage.id == id) {
                // No need to reload the image, update region only
                this.backgroundImageRoi = region;
                return;
            }

            try {
                image = !image ? await this.imageService.getImage(id) : image;
                const uri = await this.imageService.getImageUri(image);

                // Set image uri and region
                this.backgroundImage = image;
                this.backgroundImageUri = uri;
                this.backgroundImageRoi = region;
            } catch (e) {
                console.error(e);
                this.backgroundImageUri = undefined;
                this.backgroundImageRoi = undefined;
            }
        });
    }

    public distanceInputChanged(): void {
        const measurementDistance = this.computedMeasurement?.distance ?? this.selectedMeasurement?.distance ?? 0;
        this.distanceValue = measurementDistance - this.distanceInput;
        this.distanceChanged();
    }

    public updateVisibleProjections(): void {
        // If no projections computed, do nothing
        if (!this.currentProjections) {
            return;
        }

        // If we have a cached projection, but we don't show it yet in the UI, we need to update the UI
        const doesNotShowAllSelectedProjections = this.projectionStates.some(
            (state) =>
                state.selected &&
                this.currentProjections.projections.find((x) => x.properties.mode === state.mode) === undefined
        );

        const showsUnselectedProjections = this.projectionStates.some(
            (state) =>
                !state.selected &&
                this.currentProjections.projections.find((x) => x.properties.mode === state.mode) !== undefined
        );

        const shouldShowNewState = doesNotShowAllSelectedProjections || showsUnselectedProjections;

        if (shouldShowNewState) {
            const calculatedProjections: HolographyProjection[] = this.projectionStates
                .filter((x) => x.selected && x.calculatedProjection !== undefined)
                .map((x) => x.calculatedProjection);

            this.grid = this.generateMeasurementGrid(
                this.selectedMeasurement,
                calculatedProjections[0] ? calculatedProjections[0].data.length : null
            );
            this.currentProjections = new NearfieldProjectionSetModel(
                calculatedProjections,
                this.grid,
                this.idealAspectRatio
            );

            this.updateLegend();
        }
    }

    private fullscreenElementSizeChanged = () => {
        let aspectRatio = 1;
        if (this.fullscreenElement.clientHeight != 0) {
            aspectRatio = this.fullscreenElement.clientWidth / this.fullscreenElement.clientHeight;
        }

        this.idealAspectRatio = aspectRatio;

        if (!this.currentProjections) return;

        this.currentProjections.idealAspectRatio = this.idealAspectRatio;
    };

    //#endregion

    //#region Update Functions

    private updateData = (): Promise<void> => {
        if (!this.selectedMeasurement) return Promise.resolve();

        return Promise.resolve().then(() => {
            this.progress = 0;
            if (
                this.frequencySelectionService.selectedFrequency === undefined ||
                this.frequencySelectionService.selectedFrequency === null ||
                this.hasNoProjectionModeSelected
            ) {
                return;
            }

            this.busy = true;

            const measurementChanged = this.computedMeasurement !== this.selectedMeasurement;
            const measurement = this.selectedMeasurement;
            const defaultDistance = this.useDefaultDistance;

            if (
                measurementChanged &&
                (!this.computedMeasurement ||
                    !this.selectedMeasurement ||
                    this.computedMeasurement.selectedImage !== this.selectedMeasurement.selectedImage) &&
                this.backgroundImage &&
                this.backgroundImage.id !== this.selectedMeasurement.selectedImage
            ) {
                this.backgroundImageUri = undefined;
            }

            const computeProps = this.generateHolographyProjectionAnalysisProperties();
            const recomputeCutoffColorGradient = true;

            // Get holography processor
            const processorPromise = this.measurementProcessorResolver.resolveProcessor<IHolographyProcessingService>(
                IHolographyProcessingService,
                measurement
            );

            // If we got the processor use it to compute the projection
            const fullDataPromise = processorPromise
                .then<HolographyProjection[]>((service) => {
                    return this.computeProjectionCloud(
                        measurement,
                        service,
                        computeProps,
                        recomputeCutoffColorGradient
                    );
                })
                .catch<HolographyProjection[]>(() => undefined);

            // If we didnt get it, try to take the alternative route
            const singleHologramPromise = processorPromise
                .then<HolographyProjection[]>(() => undefined)
                .catch(() => {
                    // Resolve hologram processor
                    return this.measurementProcessorResolver
                        .resolveProcessor<IHologramProcessingService>(IHologramProcessingService, measurement)
                        .then((hologramProcessor) => {
                            // Get projection
                            return this.computeProjectionLocalData(
                                measurement,
                                hologramProcessor,
                                computeProps,
                                recomputeCutoffColorGradient
                            );
                        });
                });

            const projectionPromise = Promise.all([fullDataPromise, singleHologramPromise]).then((projectionSet) => {
                const projections = projectionSet[0] === undefined ? projectionSet[1] : projectionSet[0];

                if (projections === undefined) {
                    return Promise.reject(new Error("Data is not available"));
                }

                if (typeof projections === "string") {
                    return Promise.reject(new Error(<string>projections));
                }

                // Update the computed cutoff
                this.computedCutoff = projections[0].properties.cutoff;

                // Update measurement data grid
                this.grid = this.generateMeasurementGrid(measurement, projections[0].data.length);
                this.currentProjections = new NearfieldProjectionSetModel(
                    projections,
                    this.grid,
                    this.idealAspectRatio
                );

                // Save projections
                this.projectionStates.forEach((state) => {
                    // Get projection for this mode - Is undefined if not computed
                    const projection = projections.find((x) => x.properties.mode === state.mode);

                    // If we have a projection, save it. Or save undefined if we need to clear the cache
                    if (projection !== undefined || this.shouldClearProjectionCacheOnNewCompute) {
                        state.calculatedProjection = projection;
                    }
                });

                // Update the image
                const imageId = measurement.selectedImage;
                const link = measurement.links.find((x) => x.mediaObjectId === imageId);

                let roi = Quad.unitQuad;
                if (link) {
                    const props = <TargetAndSourceRegionLinkProperties>link.properties;
                    if (props && props.sourceRegion) {
                        roi = props.sourceRegion;
                    }
                }

                this.updateBackgroundImageForUI(
                    imageId == undefined && this.backgroundImageUri != undefined ? this.backgroundImage : imageId,
                    roi
                );
            });

            // Get a new projection
            return projectionPromise
                .then(() => {
                    this.usedProperties = computeProps;

                    this.updateVisibleProjections();
                    this.computedMeasurement = measurement;
                    this.selectedMeasurementIdsChanged();

                    if (defaultDistance) {
                        this.distance = computeProps.properties[0].distance;
                    }

                    this.distanceChanged();

                    this.updateLegend();

                    // Generate a timeline event
                    this.moduleInstance.createTimelineEvent().then(() => {});
                })
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(
                            "Unable to compute near field projection. The following message was provided: '" +
                                e.message +
                                "'."
                        )
                        .show();
                })
                .then(() => {
                    this.busy = false;
                    this.progress = 0;
                });
        });
    };

    private computeProjectionCloud(
        measurement: GenericMeasurement,
        service: IHolographyProcessingService,
        properties: HolographyProjectionAnalysisProperties,
        updateSlider: boolean
    ): Promise<HolographyProjection[]> {
        if (updateSlider) {
            // Update slider async, no need to wait for it
            this.updateSliderColorIndicatorCloud(measurement, service, properties);
        }

        return service.getHolographyProjections(measurement, properties, (progress: number) => {
            this.progress = progress * 100;
        });
    }

    private computeProjectionLocalData(
        measurement: GenericMeasurement,
        hologramService: IHologramProcessingService,
        properties: HolographyProjectionAnalysisProperties,
        updateSlider: boolean
    ): Promise<HolographyProjection[]> {
        // Start by getting the hologram
        const frequencyRanges = properties.properties.map((x) => ({
            from: x.frequency,
            to: x.frequency,
        }));

        return hologramService
            .getHolograms(measurement, { frequencyRanges: frequencyRanges }, (progress: number) => {
                this.progress = (progress * 100) / 2;
            })
            .then((hologramSet) => {
                const props: HolographyProjectionHologramAnalysisProperties = {
                    holograms: hologramSet,
                    properties: properties.properties,
                };

                if (updateSlider) {
                    // Update slider async, no need to wait for it
                    this.updateSliderColorIndicatorLocal(measurement, props);
                }

                // Compute holography projection
                return this.holographyHologramProcessor.getHolographyProjections(
                    measurement,
                    props,
                    (progress: number) => {
                        this.progress = (0.5 + progress / 2) * 100;
                    }
                );
            });
    }

    private calculateGridParts(measurementPoints: Point3D[], projectionSize: number): { x: number; y: number } {
        this.imagePartsAndSize.forEach((element) => {
            let i = 0,
                temporary: Point3D[],
                chunk = element;
            temporary = measurementPoints.slice(i, i + chunk);
            i += chunk;

            const uraProps = URA.getUraProperties3D(temporary);
            if (!uraProps.isUra) throw new Error("Measurement is not an URA");

            const count = uraProps.widthInElements * uraProps.heightInElements;
            const interpolationFactor = Math.round(Math.sqrt(projectionSize / this.imagePartsAndSize.length / count));

            this.partsDimension.push(
                new Grid(
                    {
                        x: uraProps.widthInElements * interpolationFactor,
                        y: uraProps.heightInElements * interpolationFactor,
                    },
                    { x: uraProps.spacing / interpolationFactor, y: uraProps.spacing / interpolationFactor },
                    { x: 0, y: 0 },
                    Length.meter
                )
            );
        });

        this.gridPartSize.height = this.partsDimension[0].dimension.y;

        if (this.partsDimension[0].dimension) {
            return this.partsDimension[0].dimension;
        }
    }

    private generateMeasurementGrid(measurement: GenericMeasurement, projectionSize: number): Grid {
        // Determine URA properties of the measurement
        const measurementPoints = measurement.parts
            .filter((x) => x.measurementType === MeasurementPartType.Audio)
            .map((part, i) => {
                return part.device.sensorPositions.map((sensor, j) => {
                    this.imagePartsAndSize[i] = j + 1;
                    return <Point3D>{
                        x: sensor.x + part.position.x,
                        y: sensor.y + part.position.y,
                        z: sensor.z + part.position.z,
                    };
                });
            })
            .reduce((p, c) => p.concat(c));

        const singleMatrixSize: { x: number; y: number } = this.calculateGridParts(
            [...measurementPoints],
            projectionSize
        );

        const uraProps = URA.getUraProperties3D(measurementPoints);
        if (!uraProps.isUra) throw new Error("Measurement is not an URA");

        const count = uraProps.widthInElements * uraProps.heightInElements;
        const interpolationFactor = Math.round(Math.sqrt(projectionSize / count));

        this.matrixSize.width = (uraProps.widthInElements * interpolationFactor) / singleMatrixSize.x;
        this.matrixSize.height = (uraProps.heightInElements * interpolationFactor) / singleMatrixSize.y;

        return new Grid(
            { x: uraProps.widthInElements * interpolationFactor, y: uraProps.heightInElements * interpolationFactor },
            { x: uraProps.spacing / interpolationFactor, y: uraProps.spacing / interpolationFactor },
            { x: 0, y: 0 },
            Length.meter
        );
    }

    private updateSliderColorIndicatorCloud(
        measurement: GenericMeasurement,
        service: IHolographyProcessingService,
        props: HolographyProjectionAnalysisProperties
    ): Promise<void> {
        return service
            .getSpatialEnergyCurve(measurement, {
                distance: props.properties[0].distance,
                properties: [
                    {
                        frequency: props.properties[0].frequency,
                    },
                ],
                useSNR: this.useSNR,
            })
            .then((curveSet) => {
                this.currentSliderColorCurve = curveSet;
            })
            .catch((e) => {
                /* We dont want this to break anything, but print the error to the console */ console.error(e);
            });
    }

    private updateSliderColorIndicatorLocal(
        measurement: GenericMeasurement,
        props: HolographyProjectionHologramAnalysisProperties
    ): Promise<void> {
        return this.holographyHologramProcessor
            .getSpatialEnergyCurve(measurement, {
                distance: props.properties[0].distance,
                holograms: props.holograms,
                useSNR: this.useSNR,
            })
            .then((curveSet) => {
                this.currentSliderColorCurve = curveSet;
            })
            .catch((e) => {
                /* We dont want this to break anything, but print the error to the console */ console.error(e);
            });
    }

    private updateBackgroundImageForMeasurement = (image: Image, regionOfInterest: Quad): void => {
        const promise = this.backgroundImageUpdatePromise ? this.backgroundImageUpdatePromise : Promise.resolve();
        const measurement = this.selectedMeasurement;

        this.backgroundImageUpdatePromise = promise.then(async () => {
            if (!measurement) {
                // No measurement means no update..
                this.updateBackgroundImageForUI(undefined, undefined);
                return;
            }

            if (!image) {
                try {
                    await this.measurementRepository.deleteSelectedImage(measurement);
                } catch (e) {
                    // An error occurred while deleting the selected image, ignore this and just unset the image..
                    console.error(e);
                }

                this.updateBackgroundImageForUI(undefined, undefined);
            } else if (image.type === ImageStorageType.Cloud) {
                try {
                    // Update the selected image in the cloud
                    await this.measurementRepository.updateSelectedImage(measurement, image.id, {
                        sourceRegion: regionOfInterest,
                        targetRegion: Quad.unitQuad,
                        layer: 0,
                    });
                    this.updateBackgroundImageForUI(image, regionOfInterest);
                } catch (e) {
                    console.error(e);

                    // An error occurred while updating the selected image, ignore this and just set the image..
                    this.updateBackgroundImageForUI(image, regionOfInterest);
                }
            }
        });
    };

    private updateLegend(): void {
        this.legendArray = [
            {
                title: "Frequency",
                entries: [{ name: this.usedFrequency.toFixed(1) + " Hz" }],
                position: 0,
            },
            {
                title: "Filter Cut-off",
                entries: [{ name: this.computedCutoff.toFixed(1) + (this.useSNR ? "dB" : "rad/m") }],
                position: 2,
            },
            {
                title: "Distance",
                entries: [{ name: Number(this.usedDistance).toFixed(3) + " m" }],
                position: 4,
            },
        ];
    }

    //#endregion
    //#region Export

    private phaseToAnimationTime = (phase: UnitValue): UnitValue => {
        return new UnitValue(phase.value / 120, Time.second);
    };

    // Required by workflowModuleInstance
    public exportVisual(): ImageExportState {
        if (!this.currentProjections) return;

        //Check if automatic Cutoff is selected
        const cutoff = this.computedCutoff.toFixed(1) + (this.useSNR ? "dB" : "radpm");

        let currentProjections = "";

        this.currentProjections.projections.forEach((x, i) => {
            if (i < this.currentProjections.projections.length - 1)
                currentProjections += HolographyProjectionMode[x.properties.mode] + "-";
            else currentProjections += HolographyProjectionMode[x.properties.mode];
        });

        const exportModalState: ImageExportState = {
            initialFileName: this.computedMeasurement ? this.computedMeasurement.name + "_NearField" : undefined,
            initialLogoPosition: LogoPositions.topRight,
            measurementIds: [this.computedMeasurement.id],
            isLegendDisplayed: false,
            animator: this.animator,
            exportable: this.currentProjections,
            timeBaseConverter: this.phaseToAnimationTime,
            namedProperties: [
                { name: "[Projection Mode]", value: currentProjections },
                { name: "[Frequency]", value: this.usedFrequency.toFixed(0) + Frequency.hertz.unit },
                { name: "[Cutoff]", value: cutoff },
                { name: "[Distance]", value: this.usedDistance + Length.meter.unit },
            ],
        };

        return exportModalState;
    }

    // Required by the export modal
    public csvExportCall = this.exportData;

    public exportData(): void {
        if (!this.currentProjections || this.currentProjections.projections.length === 0 || !this.grid) return;

        const exporter = new CSVDataExporter();
        this.currentProjections.export(
            exporter,
            "nearfield_" + MeasurementExtensions.toFileName(this.computedMeasurement)
        );
    }

    public openNearfieldSettingsModal(): void {
        if (this.nearfieldSettingsModal && this.nearfieldSettingsModal.isOpen) {
            this.nearfieldSettingsModal.cancel();
            this.nearfieldSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<NearfieldModule>, void>(WorkflowModalCatalog.NearfieldSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.nearfieldSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.nearfieldSettingsModal = x;
            });
    }

    public openExportModal(): void {
        if (this.nfExportModal && this.nfExportModal.isOpen) {
            this.nfExportModal.cancel();
            this.nfExportModal = undefined;
            return;
        }

        this.imageState.state = this.moduleInstance.getExportImageState();
        this.imageState.moduleInstance = this.moduleInstance;

        void this.moduleInstance
            .createModal<ExportModalState<IExportModule>, void>(WorkflowModalCatalog.ExportModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.nfExportModal = undefined;
            })
            .show()
            .then((x) => {
                this.nfExportModal = x;
            });
    }
    //#endregion
}
